import type { Asset, AssetClientContext, UploadPhase as ClientPhase } from "@pej/asset-client";
import { assetKeys, uploadAsset } from "@pej/asset-client";
import type { UploadPhase as FieldPhase } from "@pej/kore/components";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { toFieldPhase } from "./asset-projections";

/**
 * Drives one upload and reports it in the shape `AssetField` renders.
 *
 * Upload is the one asset operation that is not request/response: it hashes,
 * transfers, then waits on server-side verification, and the field shows all
 * three. The client emits those as phases; this hook holds the latest one and
 * projects it (see `toFieldPhase` for why the two unions differ).
 *
 * `ready` is the interesting phase, and the reason `onReady` exists rather than
 * a returned value: a finished upload is not a phase the field renders, it is
 * the field taking a value. The host writes that value into the form.
 */
export function useAssetUpload(ctx: AssetClientContext | null, onReady: (asset: Asset) => void) {
    const queryClient = useQueryClient();
    const [clientPhase, setClientPhase] = useState<ClientPhase | null>(null);
    const [totalBytes, setTotalBytes] = useState(0);
    const abortRef = useRef<AbortController | null>(null);
    /**
     * Upload outlives a fast navigation, and setting state on an unmounted
     * component is a warning at best and a leak at worst. The abort in cleanup
     * also stops the transfer itself, which is the honest behaviour: nothing is
     * left to receive the result.
     */
    const aliveRef = useRef(true);

    useEffect(() => {
        aliveRef.current = true;
        return () => {
            aliveRef.current = false;
            abortRef.current?.abort();
        };
    }, []);

    const start = useCallback(
        async (files: FileList, folderId: string | null) => {
            // Single-image slot: the field holds one asset, so a multi-file drop or selection
            // takes the first and ignores the rest by design.
            const file = files.item(0);
            if (!ctx || !file) return;

            abortRef.current?.abort();
            const controller = new AbortController();
            abortRef.current = controller;
            setTotalBytes(file.size);

            try {
                const asset = await uploadAsset(ctx, {
                    file,
                    displayName: file.name,
                    folderId,
                    signal: controller.signal,
                    onPhase: (phase) => {
                        if (aliveRef.current) setClientPhase(phase);
                    },
                });
                if (!aliveRef.current) return;
                // `uploadAsset` resolves with the SETTLED asset — READY or
                // REJECTED — and throws only on transport failure. So a
                // resolved promise is not a success, and taking it as one would
                // put a rejected asset in a field that holds only ready ones
                // (integration design, decision 6).
                //
                // On rejection the client has already emitted a `rejected`
                // phase through `onPhase`; leave it on screen. Clearing it here
                // would erase the explanation before it could be read.
                await queryClient.invalidateQueries({ queryKey: assetKeys.all(ctx.workspaceId) });
                if (asset.state !== "READY") return;
                setClientPhase(null);
                onReady(asset);
            } catch {
                // Aborted, or the upload failed outright. The last phase the
                // client reported stays on screen; replacing it with a generic
                // error would discard the more specific thing it already said.
                if (aliveRef.current && controller.signal.aborted) setClientPhase(null);
            }
        },
        [ctx, onReady, queryClient],
    );

    const cancel = useCallback(() => {
        abortRef.current?.abort();
        setClientPhase(null);
    }, []);

    const phase: FieldPhase = toFieldPhase(clientPhase, totalBytes);
    return { phase, start, cancel };
}
